/**
 * MarketDataProvider
 *
 * Connects to the dextoro-market-data WebSocket and applies instant cache
 * patches to React Query on every trade-update message.
 *
 * Replaces the old pattern of invalidateHomepageQueries() which triggered
 * full refetches (Supabase + RPC + market-data API) on every trade.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MarketDataService, type TradeUpdate } from '@/services/market-data/MarketDataService';
import type { TokenWithPoolInfo } from '@/hooks/useAllTokensWithPoolInfo';
import type { TrendingToken } from '@/hooks/useTrendingTokens';
import type { BatchPriceChanges } from '@/lib/tradingview/api';
import type { ProtocolStatsData } from '@/hooks/useProtocolStats';
import { calculateTrendingScore } from '@/lib/trending/scoreTrending';
import { getSOLPriceSync } from '@/hooks/useSOLPrice';

// SOL price for FDV calculations - refreshed periodically by the hooks themselves
const TOTAL_SUPPLY = 1_000_000_000;

export function MarketDataProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const serviceRef = useRef<MarketDataService | null>(null);

  useEffect(() => {
    const service = MarketDataService.getInstance();
    serviceRef.current = service;
    service.connect();

    const unsub = service.onTradeUpdate((update: TradeUpdate) => {
      // 1. Patch all-tokens-with-pool-info cache
      queryClient.setQueryData<TokenWithPoolInfo[]>(
        ['all-tokens-with-pool-info'],
        (old) => {
          if (!old) return old;
          let found = false;
          const updated = old.map((token) => {
            if (token.poolAddress !== update.poolAddress) return token;
            found = true;
            const fdv = update.price * TOTAL_SUPPLY;
            // Preserve existing solPrice ratio, fall back to cached SOL price
            const solPrice = (token.fdvUsd && token.fdv) ? token.fdvUsd / token.fdv : getSOLPriceSync();
            return {
              ...token,
              currentPrice: update.price,
              fdv,
              fdvUsd: fdv * solPrice,
              priceChange5m: update.priceChange5m ?? token.priceChange5m,
              priceChange1h: update.priceChange1h ?? token.priceChange1h,
              volume24h: (token.volume24h ?? 0) + (update.volume * solPrice),
            };
          });
          return found ? updated : old;
        }
      );

      // 2. Patch batch-price-changes cache
      queryClient.setQueryData<BatchPriceChanges>(
        ['batch-price-changes'],
        (old) => {
          if (!old) return old;
          if (!old[update.poolAddress] && update.priceChange5m == null) return old;
          return {
            ...old,
            [update.poolAddress]: {
              change5m: update.priceChange5m ?? old[update.poolAddress]?.change5m ?? null,
              change1h: update.priceChange1h ?? old[update.poolAddress]?.change1h ?? null,
              change6h: update.priceChange6h ?? old[update.poolAddress]?.change6h ?? null,
            },
          };
        }
      );

      // 3. Patch trending-tokens cache: update price, volume, and recalculate score
      queryClient.setQueriesData<TrendingToken[]>(
        { queryKey: ['trending-tokens'] },
        (old) => {
          if (!old) return old;
          let found = false;
          const updated = old.map((token) => {
            if (token.poolAddress !== update.poolAddress) return token;
            found = true;
            const fdv = update.price * TOTAL_SUPPLY;
            const solPrice = (token.fdvUsd && token.fdv) ? token.fdvUsd / token.fdv : getSOLPriceSync();

            // Accumulate volume and trade count from this trade
            const newVolume = (token.volume24h ?? 0) + update.volume;
            const newTradeCount = token.tradeCount24h + 1;

            // Recalculate trending score with updated metrics
            const trendingScore = calculateTrendingScore(
              newVolume,
              newTradeCount,
              token.holderCount,
              token.createdAt
            );

            return {
              ...token,
              currentPrice: update.price,
              fdv,
              fdvUsd: fdv * solPrice,
              priceChange5m: update.priceChange5m ?? token.priceChange5m,
              priceChange1h: update.priceChange1h ?? token.priceChange1h,
              volume24h: newVolume,
              tradeCount24h: newTradeCount,
              trendingScore,
            };
          });
          if (!found) return old;
          // Re-sort by trending score so the carousel reflects new rankings
          return updated.sort((a, b) => b.trendingScore - a.trendingScore);
        }
      );

      // 4. Patch protocol-stats: increment volume optimistically
      queryClient.setQueryData<ProtocolStatsData>(
        ['protocol-stats'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            totalVolume: old.totalVolume + update.volume,
          };
        }
      );
    });

    return () => {
      unsub();
      service.disconnect();
    };
  }, [queryClient]);

  return <>{children}</>;
}
